
import * as Sentry from '@sentry/react';

/**
 * Breadcrumb levels following Sentry's conventions
 */
export type BreadcrumbLevel = 'debug' | 'info' | 'warning' | 'error' | 'fatal';

/**
 * Common categories for our application
 */
export type BreadcrumbCategory = 
  | 'auth'
  | 'customer'
  | 'site'
  | 'address'
  | 'floor'
  | 'room'
  | 'outlet'
  | 'wireless'
  | 'network'
  | 'tenant'
  | 'user'
  | 'api'
  | 'navigation'
  | 'ui';

/**
 * Add a breadcrumb to track user actions and system events
 */
export const addBreadcrumb = (
  category: BreadcrumbCategory,
  message: string,
  level: BreadcrumbLevel = 'info',
  data?: Record<string, unknown>
) => {
  Sentry.addBreadcrumb({
    category,
    message,
    level,
    data,
    timestamp: Date.now() / 1000, // Sentry expects timestamp in seconds
  });
};

/**
 * Log authentication related events
 */
export const logAuth = (message: string, data?: Record<string, unknown>) => {
  addBreadcrumb('auth', message, 'info', data);
};

/**
 * Log customer related events
 */
export const logCustomer = (message: string, data?: Record<string, unknown>) => {
  addBreadcrumb('customer', message, 'info', data);
};

/**
 * Log site related events
 */
export const logSite = (message: string, data?: Record<string, unknown>) => {
  addBreadcrumb('site', message, 'info', data);
};

/**
 * Log address related events
 */
export const logAddress = (message: string, data?: Record<string, unknown>) => {
  addBreadcrumb('address', message, 'info', data);
};

/**
 * Log floor related events
 */
export const logFloor = (message: string, data?: Record<string, unknown>) => {
  addBreadcrumb('floor', message, 'info', data);
};

/**
 * Log room related events
 */
export const logRoom = (message: string, data?: Record<string, unknown>) => {
  addBreadcrumb('room', message, 'info', data);
};

/**
 * Log outlet related events
 */
export const logOutlet = (message: string, data?: Record<string, unknown>) => {
  addBreadcrumb('outlet', message, 'info', data);
};

/**
 * Log wireless related events
 */
export const logWireless = (message: string, data?: Record<string, unknown>) => {
  addBreadcrumb('wireless', message, 'info', data);
};

/**
 * Log network related events
 */
export const logNetwork = (message: string, data?: Record<string, unknown>) => {
  addBreadcrumb('network', message, 'info', data);
};

/**
 * Log tenant related events
 */
export const logTenant = (message: string, data?: Record<string, unknown>) => {
  addBreadcrumb('tenant', message, 'info', data);
};

/**
 * Log user related events
 */
export const logUser = (message: string, data?: Record<string, unknown>) => {
  addBreadcrumb('user', message, 'info', data);
};

/**
 * Log navigation events
 */
export const logNavigation = (path: string, title?: string) => {
  addBreadcrumb('navigation', `Navigated to ${title || path}`, 'info', { path });
};

/**
 * Log CRUD operations
 */
export const logOperation = (
  category: BreadcrumbCategory,
  operation: 'create' | 'read' | 'update' | 'delete',
  entityType: string,
  entityId?: string | number,
  details?: Record<string, unknown>
) => {
  const message = `${operation.charAt(0).toUpperCase() + operation.slice(1)}d ${entityType}${entityId ? ` (ID: ${entityId})` : ''}`;
  
  addBreadcrumb(category, message, 'info', {
    operation,
    entityType,
    entityId,
    ...details
  });
};
